import { Section, Overline, SectionHeading } from "../components/Section";
import { PROCESS_STEPS, TRUST_METRICS, SERVICES, SPECIALTIES } from "../data/site";
import ConsultationForm from "../components/ConsultationForm";
import ConciergePlans from "../components/ConciergePlans";
import WhatsAppButton from "../components/WhatsAppButton";
import { WA_DISPLAY, waLink } from "../lib/whatsapp";
import { MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";

const CONCIERGE_IMG = "https://images.unsplash.com/photo-1660557989695-14fac79c086d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjb25jaWVyZ2UlMjBkZXNrfGVufDB8fHx8MTc4NDI5NzgyN3ww&ixlib=rb-4.1.0&q=85";
const COUPLE_IMG = "https://images.unsplash.com/photo-1746192703468-dedf3fc11d87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzZW5pb3IlMjBjb3VwbGUlMjBzbWlsaW5nfGVufDB8fHx8MTc4NDI5NzgyN3ww&ixlib=rb-4.1.0&q=85";

export function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <section className="relative bg-[#0B1F3A] text-white pt-40 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Overline className="text-[#C8A96A]">{eyebrow}</Overline>
        <h1 className="mt-6 font-serif font-light text-white text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-8 max-w-2xl text-white/75 text-[16px] leading-relaxed">{subtitle}</p>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 gold-rule opacity-70" />
    </section>
  );
}

export function AboutPage() {
  return (
    <>
      <PageHeader
        eyebrow="About Easyilaaz"
        title="A private concierge — for a private matter."
        subtitle="We are India's premier private medical concierge and healthcare navigation platform, representing patients and their families through every stage of their care."
      />
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7">
            <Overline>Our position</Overline>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#0B1F3A] leading-tight">
              Independent, by design.
            </h2>
            <div className="mt-8 space-y-6 text-[15px] leading-[1.9] text-[#0B1F3A]/80 max-w-2xl">
              <p>Easyilaaz is not a hospital. Easyilaaz is not a clinic. Easyilaaz is not a discount medical tourism company.</p>
              <p>We are an independent healthcare concierge that represents patients and their families. We help NRIs and international patients receive seamless access to India's leading hospitals and specialists through a premium, white-glove concierge experience.</p>
              <p>Our role is to simplify healthcare — by managing every non-clinical aspect of the journey, so doctors and hospitals may focus on treatment, and families may focus on presence.</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={CONCIERGE_IMG} alt="Concierge" className="w-full h-[540px] object-cover" />
          </div>
        </div>
      </Section>

      <Section className="bg-[#F5F7FA]">
        <SectionHeading eyebrow="Our Mission" title="To remove complexity, uncertainty, and stress from accessing healthcare in India." />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          {TRUST_METRICS.map((m) => (
            <div key={m.label}>
              <div className="font-serif text-4xl lg:text-5xl text-[#0B1F3A]">{m.value}</div>
              <div className="mt-2 overline text-[#0B1F3A]/50">{m.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5"><img src={COUPLE_IMG} className="w-full h-[520px] object-cover" alt="" /></div>
          <div className="lg:col-span-7">
            <Overline>Who we serve</Overline>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#0B1F3A] leading-tight">Guests. Never patients-in-transit.</h2>
            <p className="mt-8 text-[15px] leading-[1.9] text-[#0B1F3A]/80 max-w-2xl">
              Our members are NRIs, business owners, senior citizens and families
              seeking care in India. They come from the United States, Canada, the
              United Kingdom, the Emirates, Australia, and 17 other countries.
              They come to us because they wish to be represented — not routed.
            </p>
          </div>
        </div>
      </Section>
    </>
  );
}

export function ProcessPage() {
  return (
    <>
      <PageHeader
        eyebrow="Our Concierge Process"
        title="Five quiet steps, considered from beginning to end."
        subtitle="From the first message to the final follow-up, your concierge coordinates every non-clinical detail — privately."
      />
      <Section className="bg-white">
        <div className="space-y-24">
          {PROCESS_STEPS.map((s, i) => (
            <div key={s.n} data-testid={`process-detail-${i+1}`} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-3">
                <div className="font-serif text-7xl lg:text-8xl text-[#C8A96A]">{s.n}</div>
              </div>
              <div className="lg:col-span-9 lg:pt-4">
                <h3 className="font-serif text-4xl lg:text-5xl text-[#0B1F3A] leading-tight">{s.title}</h3>
                <p className="mt-6 text-[15px] leading-[1.9] text-[#0B1F3A]/75 max-w-2xl">{s.body}</p>
              </div>
              {i < PROCESS_STEPS.length - 1 && <div className="col-span-full gold-rule opacity-40" />}
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function ServicesPage() {
  return (
    <>
      <PageHeader
        eyebrow="Services"
        title="Every detail — quietly attended to."
        subtitle="Eight core services delivered by one dedicated concierge. Available as bespoke engagements or through membership."
      />
      <Section className="bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E6E9EF]">
          {SERVICES.map((s, i) => (
            <div key={s.title} className="bg-white p-10 lg:p-14">
              <div className="overline text-[#C8A96A]">0{i+1}</div>
              <h3 className="mt-5 font-serif text-3xl text-[#0B1F3A]">{s.title}</h3>
              <p className="mt-4 text-[14px] leading-relaxed text-[#0B1F3A]/75">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function SpecialtiesPage() {
  return (
    <>
      <PageHeader
        eyebrow="Medical Specialties"
        title="Four disciplines. Curated with care."
        subtitle="We launch with the four specialties most requested by our members, expanding through 2026 into oncology, neurology, organ transplant, gastroenterology, robotic surgery and preventive health."
      />
      <Section className="bg-white">
        <div className="space-y-24">
          {SPECIALTIES.map((s, i) => (
            <div key={s.key} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-4">
                <div className="overline text-[#C8A96A]">0{i+1}</div>
                <h3 className="mt-4 font-serif text-4xl lg:text-5xl text-[#0B1F3A] leading-tight">{s.name}</h3>
                <div className="mt-3 font-serif italic text-xl text-[#0B1F3A]/60">{s.tagline}</div>
              </div>
              <div className="lg:col-span-8 lg:pt-4">
                <p className="text-[15px] leading-[1.9] text-[#0B1F3A]/80 max-w-2xl">{s.body}</p>
                <Link to="/contact" className="mt-8 inline-flex items-center h-11 px-6 rounded-full border border-[#0B1F3A] text-[#0B1F3A] text-[11px] tracking-[0.18em] uppercase hover:bg-[#0B1F3A] hover:text-white transition-colors duration-300">
                  Enquire About {s.name}
                </Link>
              </div>
              {i < SPECIALTIES.length - 1 && <div className="col-span-full gold-rule opacity-40" />}
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function MembershipPage() {
  return (
    <>
      <PageHeader
        eyebrow="Concierge Plans"
        title="Three tiers. One promise of care."
        subtitle="Every plan begins with a private conversation. Fees are shared confidentially following an initial consultation."
      />
      <Section className="bg-white">
        <ConciergePlans />
      </Section>
    </>
  );
}

export function ContactPage() {
  return (
    <>
      <PageHeader
        eyebrow="Request Consultation"
        title="A private line to your concierge."
        subtitle="Share as much or as little as you wish. A dedicated concierge will respond within one business day — privately, and without obligation."
      />
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Overline>Concierge Desk</Overline>
            <div className="mt-6 space-y-6 text-[14px] text-[#0B1F3A]/80">
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B1F3A]/50">Email</div>
                <div className="mt-2 text-[15px]">concierge@easyilaaz.com</div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B1F3A]/50">WhatsApp — 24 × 7</div>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="mt-2 inline-flex items-center gap-2 text-[15px] link-elegant"
                >
                  <MessageSquareText size={14} strokeWidth={1.25} className="text-[#C8A96A]" />
                  {WA_DISPLAY}
                </a>
              </div>
              <div className="gold-rule max-w-xs" />
              <div>
                <WhatsAppButton
                  variant="ghost"
                  testId="contact-whatsapp-cta"
                  message="Hello Easyilaaz Concierge — I would like to arrange a private consultation."
                >
                  Message on WhatsApp
                </WhatsAppButton>
              </div>
              <p className="text-[12px] leading-relaxed text-[#0B1F3A]/55 max-w-xs">
                All information shared with Easyilaaz is handled in strict confidence
                and used solely to coordinate your care.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ConsultationForm />
          </div>
        </div>
      </Section>
    </>
  );
}

export function LegalPage({ kind }) {
  const content = {
    privacy: {
      title: "Privacy Policy",
      body: "Easyilaaz treats all personal and medical information as strictly confidential. Data shared with us is used solely to coordinate the guest's care and is never shared outside the assigned care team without written consent. Members may request access, correction, or deletion of their information at any time by writing to concierge@easyilaaz.com.",
    },
    terms: {
      title: "Terms & Conditions",
      body: "Easyilaaz is a private healthcare concierge and navigation service. Our engagements are governed by the terms shared privately with each member on assignment. Nothing on this website constitutes an offer, a guarantee of outcome, or medical advice.",
    },
    disclaimer: {
      title: "Medical Disclaimer",
      body: "Easyilaaz does not diagnose or treat medical conditions. All medical advice, diagnosis and treatment are provided exclusively by licensed doctors and accredited hospitals in our network. Easyilaaz coordinates and facilitates the patient's journey. In case of a medical emergency, please contact local emergency services immediately.",
    },
  }[kind];

  return (
    <>
      <PageHeader eyebrow="Legal" title={content.title} />
      <Section className="bg-white">
        <div className="max-w-3xl">
          <p className="text-[15px] leading-[1.9] text-[#0B1F3A]/80">{content.body}</p>
          <p className="mt-8 text-[13px] text-[#0B1F3A]/50">Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long" })}.</p>
        </div>
      </Section>
    </>
  );
}
